// src/pages/Organizations/Marketing/OrganizationMarketingStudio.tsx
import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Button, Grid, CircularProgress, Divider, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CampaignIcon from '@mui/icons-material/Campaign';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useMarketingStudio } from '../../../contexts/MarketingStudioContext';

// Import Marketing Studio components
import AssetGenerator from './components/AssetGenerator';
import ContentGenerator from './components/ContentGenerator';
import CampaignManager from './components/CampaignManager';
import MarketingAnalytics from './components/MarketingAnalytics';

// TabPanel component for handling tab content
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`marketing-studio-tabpanel-${index}`}
      aria-labelledby={`marketing-studio-tab-${index}`}
      {...other}
      style={{ padding: '20px 0' }}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `marketing-studio-tab-${index}`,
    'aria-controls': `marketing-studio-tabpanel-${index}`,
  };
}

const OrganizationMarketingStudio: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentOrganization, loadingOrganization: orgLoading } = useOrganization();
  const { 
    organizationBranding, 
    aiProvider, 
    setAIProvider,
    loading: studioLoading 
  } = useMarketingStudio();
  
  const [tabValue, setTabValue] = useState(0);
  
  useEffect(() => {
    if (!id) {
      navigate('/organizations');
    }
  }, [id, navigate]);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  if (orgLoading || studioLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!currentOrganization) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error">Organization not found</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: theme.palette.background.paper }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Marketing Studio
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create, manage, and analyze marketing assets and campaigns for {currentOrganization.name}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              AI Provider: <span style={{ fontWeight: 'bold' }}>{aiProvider.charAt(0).toUpperCase() + aiProvider.slice(1)} AI</span>
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setTabValue(0)}
              sx={{ mr: 1 }}
            >
              New Asset
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          aria-label="marketing studio tabs"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': { py: 2 }
          }}
        >
          <Tab 
            icon={<ImageIcon />} 
            label="Asset Generation" 
            {...a11yProps(0)} 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={<TextFieldsIcon />} 
            label="Content Creation" 
            {...a11yProps(1)} 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={<CampaignIcon />} 
            label="Campaign Manager" 
            {...a11yProps(2)} 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={<AnalyticsIcon />} 
            label="Analytics" 
            {...a11yProps(3)} 
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <AssetGenerator 
              organizationId={id || ''} 
              organizationName={currentOrganization.name}
              branding={organizationBranding}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <ContentGenerator 
              organizationId={id || ''} 
              organizationName={currentOrganization.name}
              branding={organizationBranding}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <CampaignManager 
              organizationId={id || ''} 
              organizationName={currentOrganization.name}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <MarketingAnalytics 
              organizationId={id || ''} 
              organizationName={currentOrganization.name}
            />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default OrganizationMarketingStudio;
